import { ScrollArea } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { useOnScroll } from '@mm/hooks/src/use-on-scroll';
import { getIsAutomationFeatureEnabled } from '@mm/state/selectors/automations';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  activeChatbotIdSelector,
  isRoleForActiveCompanyFBCollaboratorSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import { some } from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import CreateRemoteEntity, {
  EntityType,
} from '../../../common/components/CreateRemoteEntity/CreateRemoteEntity';
import NotificationBanners from '../../../common/components/NotificationBanners/NotificationBanners';
import * as routes from '../../../common/constants/routes';
import { DailyTwilioSoftLimitReachedModal } from '../../../common/modals/DailyTwilioSoftLimitReachedModal/DailyTwilioSoftLimitReachedModal';
import BotBuilderSidebar from '../../../layout/BotBuilderSidebar/BotBuilderSidebar';
import MobileHeader from '../../../layout/MobileHeader/MobileHeader';
import { HelpWidget } from '../../../modules/HelpWidget/HelpWidget';
import { useAmbassadorPaywall } from '../../../modules/LoginRouter/use-ambassador-paywall';
import {
  AUTOMATIONS_OLD_ROOT_PATH,
  BotBuilderRoutes,
} from '../../../routes/bot-builder-routes';
import AdConfigsPanel from '../../features/clickToMessengerAds/panels/AdConfigsPanel/AdConfigsPanel';
import RssFeedsPanel from '../../features/rssFeeds/panels/RssFeedsPanel/RssFeedsPanel';
import { TemplateByCodePanel } from '../../features/templates/panels/TemplateByCodePanel/TemplateByCodePanel';
import { TemplateLibraryPanel } from '../../features/templates/panels/TemplateLibraryPanel/TemplateLibraryPanel';
import { TemplateMyPanel } from '../../features/templates/panels/TemplateMyPanel/TemplateMyPanel';
import { TriggerPanel } from '../../features/trigger/panels/TriggerPanel/TriggerPanel';
import './ChatbotEditorPage.pcss';

export const MIN_HEADER_SCREEN_WIDTH = 1150;

export const ChatbotEditor = () => {
  const [scrollTop, setScrollTop] = useState<number>();
  const mainScrollDisabled = useSelector(
    (state: StoreState) => !!state.view.disableMainScroll,
  );

  const paywall = useAmbassadorPaywall();

  useEffect(() => {
    paywall.openPaywall();
  }, []);

  const contentContainerRef = useRef<HTMLDivElement | null>(null);

  const hasAutomations = useSelector(combinedFeatureFlagsSelector).automations;

  useEffect(() => {
    setScrollTop(contentContainerRef.current?.scrollTop);
  }, [contentContainerRef.current]);

  useOnScroll(
    contentContainerRef.current,
    () => {
      setScrollTop(contentContainerRef?.current?.scrollTop);
    },
    250,
  );

  const isSubnavVisible = useSelector((state: StoreState) =>
    some(state.view.visibleBetaSubNavs, Boolean),
  );
  const location = useLocation();
  const { screenWidth } = useResolutionInfo();

  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const pathBotId = Number(location.pathname.split('/')[2]);
  const botId = pathBotId || activeChatbotId;
  const facebookPage = useSelector(activeFacebookPageSelector);
  const sidebarMinimized = useSelector(
    (s: StoreState) => s.chatbotSidebarState.isMinimized,
  );

  const isFBCollaborator = useSelector(
    isRoleForActiveCompanyFBCollaboratorSelector,
  );
  const isFreeTrial = !!facebookPage?.redemption?.freeTrial;
  const isExpired =
    facebookPage?.redemption &&
    moment().diff(moment(facebookPage.redemption.expiresAt)) > 0;

  const isFreeTrialActive = isFreeTrial && !isExpired;
  const isAutomationsEnabled = useSelector(getIsAutomationFeatureEnabled);
  const showMobileHeader = screenWidth < MIN_HEADER_SCREEN_WIDTH;

  const contentContainerClassList = classnames(
    'chatbot-editor-page__content-container',
    {
      'chatbot-editor-page--sidebar-with-automations':
        isAutomationsEnabled && isFreeTrialActive,
      'chatbot-editor-page--sidebar-minimized': sidebarMinimized,
      'chatbot-editor-page--scroll-disabled': mainScrollDisabled,
      'chatbot-editor-page__content--subnav-open':
        isSubnavVisible && !showMobileHeader,
    },
  );

  return (
    <div
      className={classnames('app-body', {
        'main--paywall': paywall.showPaywall,
      })}
    >
      <NotificationBanners />
      <HelpWidget />

      <main className={'main'} onClickCapture={e => paywall.openPaywall(e)}>
        <ScrollArea
          id="chatbot-editor-page__content-container"
          className={contentContainerClassList}
          ref={contentContainerRef}
        >
          <DailyTwilioSoftLimitReachedModal />

          <Switch>
            <Route
              path={BotBuilderRoutes.adashboard.path}
              component={BotBuilderRoutes.adashboard.component}
            />
            <Route
              path={BotBuilderRoutes.dashboard.path}
              component={
                hasAutomations
                  ? BotBuilderRoutes.adashboard.component
                  : BotBuilderRoutes.dashboard.component
              }
            />

            <Route
              path={BotBuilderRoutes.automationEdit.path}
              component={BotBuilderRoutes.automationEdit.component}
            />

            <Route
              path={BotBuilderRoutes.automationNew.path}
              component={BotBuilderRoutes.automationNew.component}
            />
            <Route
              path={BotBuilderRoutes.automationHome.path}
              component={BotBuilderRoutes.automationHome.component}
            />
            <Redirect
              from={AUTOMATIONS_OLD_ROOT_PATH}
              to={BotBuilderRoutes.automationHome.path}
            />

            <Route
              exact={true}
              path={BotBuilderRoutes.mainMenu.path}
              component={BotBuilderRoutes.mainMenu.component}
            />

            <Route
              path={BotBuilderRoutes.build.path}
              component={BotBuilderRoutes.build.component}
            />
            <Route
              exact={true}
              path={BotBuilderRoutes.smsKeywordEdit.path}
              component={BotBuilderRoutes.smsKeywordEdit.component}
            />
            <Route
              path="/chatbot-editor/:chatbotId/trigger"
              component={TriggerPanel}
            />
            <Route
              path={BotBuilderRoutes.liveChat.path}
              component={BotBuilderRoutes.liveChat.component}
            />
            <Route
              path="/chatbot-editor/:chatbotId/templates/by-code"
              component={TemplateByCodePanel}
            />
            <Route
              path="/chatbot-editor/:chatbotId/templates/library"
              component={TemplateLibraryPanel}
            />
            <Route
              path="/chatbot-editor/:chatbotId/templates/my"
              component={TemplateMyPanel}
            />
            {!isFBCollaborator && (
              <Route
                path="/chatbot-editor/:chatbotId/templates/my"
                component={TemplateMyPanel}
              />
            )}
            <Route
              exact={true}
              path={`${BotBuilderRoutes.customerChat.path}/new`}
              render={() => (
                <CreateRemoteEntity entityType={EntityType.customerChat} />
              )}
            />
            <Route
              exact={true}
              path={BotBuilderRoutes.customerChatEdit.path}
              component={BotBuilderRoutes.customerChatEdit.component}
            />
            <Route
              exact={true}
              path={BotBuilderRoutes.customerChat.path}
              component={BotBuilderRoutes.customerChat.component}
            />
            <Route
              exact={true}
              path={BotBuilderRoutes.instagramStoryMentionEdit.path}
              component={BotBuilderRoutes.instagramStoryMentionEdit.component}
            />
            <Route
              exact={true}
              path={BotBuilderRoutes.instagramStoryReactionEdit.path}
              component={BotBuilderRoutes.instagramStoryReactionEdit.component}
            />
            <Route
              exact={true}
              path={BotBuilderRoutes.instagramLiveEdit.path}
              component={BotBuilderRoutes.instagramLiveEdit.component}
            />
            <Route
              exact={true}
              path={BotBuilderRoutes.instagramCommentGuardEdit.path}
              component={BotBuilderRoutes.instagramCommentGuardEdit.component}
            />
            <Route
              exact={true}
              path={`${BotBuilderRoutes.shareLinks.path}/new`}
              render={() => (
                <CreateRemoteEntity entityType={EntityType.shareLink} />
              )}
            />
            <Route
              path={BotBuilderRoutes.shareLinkEdit.path}
              render={() => (
                <BotBuilderRoutes.shareLinkEdit.component kind="link" />
              )}
            />
            <Route
              path={BotBuilderRoutes.shareLinks.path}
              component={BotBuilderRoutes.shareLinks.component}
            />
            <Route
              path={BotBuilderRoutes.sendToMessengerEdit.path}
              render={() => (
                <BotBuilderRoutes.sendToMessengerEdit.component kind="send_to_messenger" />
              )}
            />
            <Route
              path={BotBuilderRoutes.checkboxPluginEdit.path}
              render={() => (
                <BotBuilderRoutes.checkboxPluginEdit.component kind="checkbox" />
              )}
            />
            <Route
              path={BotBuilderRoutes.leadPopupsNew.path}
              component={BotBuilderRoutes.leadPopupsNew.component}
            />
            <Route
              path={BotBuilderRoutes.leadPopupsEdit.path}
              component={BotBuilderRoutes.leadPopupsEdit.component}
            />
            <Route
              path={BotBuilderRoutes.leadPopups.path}
              component={BotBuilderRoutes.leadPopups.component}
            />
            <Route
              path={BotBuilderRoutes.facebookOptIns.path}
              component={BotBuilderRoutes.facebookOptIns.component}
            />
            <Route
              path={BotBuilderRoutes.facebookPageWelcomer.path}
              component={BotBuilderRoutes.facebookPageWelcomer.component}
            />
            <Route
              path={BotBuilderRoutes.commentGuardEdit.path}
              component={BotBuilderRoutes.commentGuardEdit.component}
            />
            <Route
              path={BotBuilderRoutes.commentGuards.path}
              component={BotBuilderRoutes.commentGuards.component}
            />
            <Route
              path={BotBuilderRoutes.connectionsEdit.path}
              component={BotBuilderRoutes.connectionsEdit.component}
            />
            <Route
              path={BotBuilderRoutes.chatBlastEdit.path}
              component={BotBuilderRoutes.chatBlastEdit.component}
            />
            <Route
              path={BotBuilderRoutes.sponsoredMessagesEdit.path}
              component={BotBuilderRoutes.sponsoredMessagesEdit.component}
            />
            <Route
              path={BotBuilderRoutes.campaignReport.path}
              component={BotBuilderRoutes.campaignReport.component}
            />
            <Route
              path={BotBuilderRoutes.dripCampaignEdit.path}
              render={props => {
                const lastPartURL = props.location.pathname.split('/').pop();
                const campaignId =
                  lastPartURL === 'campaign-detai'
                    ? undefined
                    : Number(lastPartURL);
                const Component = BotBuilderRoutes.dripCampaignEdit.component;
                return <Component campaignId={campaignId} />;
              }}
            />
            <Route
              path="/chatbot-editor/:chatbotId/click-to-messenger-ads"
              component={AdConfigsPanel}
            />
            <Route
              path="/chatbot-editor/:chatbotId/click-to-messenger-ad-detail"
              component={AdConfigsPanel}
            />
            <Route
              path={BotBuilderRoutes.rssFeeds.path}
              component={RssFeedsPanel}
            />
            <Route
              path={BotBuilderRoutes.landingPagesEdit.path}
              component={BotBuilderRoutes.landingPagesEdit.component}
            />
            <Route
              path={BotBuilderRoutes.chatStarters.path}
              component={BotBuilderRoutes.chatStarters.component}
            />
            <Route
              path={BotBuilderRoutes.automationRules.path}
              component={BotBuilderRoutes.automationRules.component}
            />
            <Route
              path={BotBuilderRoutes.campaigns.path}
              component={BotBuilderRoutes.campaigns.component}
            />
            <Route
              path={BotBuilderRoutes.ctmAdsEdit.path}
              component={BotBuilderRoutes.ctmAdsEdit.component}
            />
            <Route
              path={BotBuilderRoutes.audiences.path}
              component={BotBuilderRoutes.audiences.component}
            />
            <Route
              path={BotBuilderRoutes.contacts.path}
              component={BotBuilderRoutes.contacts.component}
            />
            <Route
              path={BotBuilderRoutes.xRay.path}
              component={BotBuilderRoutes.xRay.component}
            />
            <Route
              path={BotBuilderRoutes.leadsSearch.path}
              component={BotBuilderRoutes.leadsSearch.component}
            />
            <Route
              path={BotBuilderRoutes.settings.path}
              component={BotBuilderRoutes.settings.component}
            />

            <Route
              path={BotBuilderRoutes.integrations.path}
              component={BotBuilderRoutes.integrations.component}
            />

            <Route
              path={BotBuilderRoutes.botAnalytics.path}
              component={BotBuilderRoutes.botAnalytics.component}
            />

            <Route
              path={BotBuilderRoutes.adsAnalytics.path}
              component={BotBuilderRoutes.adsAnalytics.component}
            />

            <Route
              path={BotBuilderRoutes.emailAnalytics.path}
              component={BotBuilderRoutes.emailAnalytics.component}
            />

            <Route
              path={BotBuilderRoutes.xrayAnalytics.path}
              component={BotBuilderRoutes.xrayAnalytics.component}
            />

            <Route
              path={BotBuilderRoutes.instagramWelcomerEdit.path}
              component={BotBuilderRoutes.instagramWelcomerEdit.component}
            />

            <Redirect
              from={routes.CHATBOT_EDITOR_ZAPIER_ANY_BOT}
              to={`/chatbot-editor/${botId}/zapier`}
            />

            <Redirect
              from={routes.CHATBOT_EDITOR}
              to={BotBuilderRoutes.dashboard.getLink({
                chatbotId: botId,
              })}
            />
          </Switch>
        </ScrollArea>
      </main>

      {showMobileHeader ? (
        <MobileHeader />
      ) : (
        <BotBuilderSidebar hideSubNav={Boolean(scrollTop && scrollTop > 20)} />
      )}
    </div>
  );
};
